// src/pages/Secretary/Patients.js
//
// Hastalar — beyaz zemin üstünde düz liste: başlık şeridi (PageHeaderBar, sağda
// "Hasta Ekle") → araç satırı (arama + doğum tarihi + bekleyen talepler) → hazır
// filtre çipleri (segment) → çerçevesiz tablo (DataTableCard variant="flat").
// Bekleyen bağlantı talepleri sağdan açılan drawer'da.
// Liste sunucu tarafında süzülür, sıralanır ve sayfalanır (listPatients).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, ButtonBase, Typography, alpha } from "@mui/material";
import {
  HourglassEmpty as HourglassIcon,
  ChevronRight as ChevronRightIcon,
  FormatListBulleted as ListIcon,
  InsertChartOutlined as ChartIcon,
} from "@mui/icons-material";
import { FaUserInjured } from "react-icons/fa";
import useAuth from "../../hooks/useAuth";
import { useHasPermission } from "../../hooks/useHasPermission";
import useDebouncedSearch from "../../hooks/useDebouncedSearch";
import { useClinicTheme } from "../../theme";
import { useAlert } from "../../context/AlertProvider";
import {
  ActionButton,
  ChoiceChipGroup,
  FormDatePicker,
  SearchField,
} from "../../components/atoms";
import { TabbedPageShell } from "../../components/organisms";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { COLORS, TYPO } from "../../styles/uiConstants";
import { useListPatientsMutation } from "../../store/secretaryApi";
import {
  useListClinicPatientLinksQuery,
  useCancelClinicPatientLinkMutation,
} from "../../store";
import AddPatientWizard from "./components/AddPatientWizard";
import PatientsTable from "./PatientsPage/PatientsTable";
import PendingRequestsDrawer from "./PatientsPage/PendingRequestsDrawer";
import PatientDistributionTab from "./PatientsPage/PatientDistributionTab";
import useTerms from "../../hooks/useTerms";
import { useClinicModule } from "../../hooks/useClinicModule";

const TABS = [
  {
    key: "liste",
    label: "Hasta Listesi",
    icon: <ListIcon sx={{ fontSize: 16 }} />,
  },
  {
    key: "dagilim",
    label: "Hasta Dağılımı",
    icon: <ChartIcon sx={{ fontSize: 16 }} />,
  },
];

/** Hazır filtre seçiliyken sonuç boşsa gösterilecek açıklama. */
const segmentEmpty = (terms) => ({
  today: `Bugün randevusu olan ${terms.patientLower} yok.`,
  activeTreatment: `Şu an aktif tedavisi süren ${terms.patientLower} yok.`,
  inactive:
    `Tüm ${terms.patientsLower} son 90 gün içinde gelmiş ya da ileri tarihli randevusu var.`,
});

const SEGMENTS = [
  { value: "all", label: "Tümü" },
  { value: "today", label: "Bugün randevulu" },
  { value: "activeTreatment", label: "Aktif tedavide" },
  { value: "inactive", label: "90+ gündür gelmeyen" },
];

/** Varsayılan yön: tarih alanlarında en yakın/yeni önce, isimde A→Z. */
const DEFAULT_DIR = { lastVisit: "desc", nextAppointment: "asc", name: "asc" };

export default function Patients() {
  const terms = useTerms();
  const treatmentModule = useClinicModule("treatment");
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const navigate = useNavigate();
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();

  const isSecretary =
    auth?.role === "Secretary" || auth?.role === "ClinicStaff";
  const canCreatePatient = useHasPermission("clinicPatientLink:create");

  const [wizardOpen, setWizardOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [cancelLinkId, setCancelLinkId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [listPatients, { isLoading }] = useListPatientsMutation();
  const [cancelLink, { isLoading: cancelling }] =
    useCancelClinicPatientLinkMutation();

  const {
    data: pendingLinkData,
    isLoading: pendingLoading,
    refetch: refetchPending,
  } = useListClinicPatientLinksQuery(
    { token, group: "pending" },
    { skip: !token, refetchOnMountOrArgChange: true },
  );
  const pendingLinks = useMemo(
    () => pendingLinkData?.data?.links || [],
    [pendingLinkData],
  );

  // Tek arama kutusu: yalnız rakam → TC, aksi halde ad/soyad
  const [search, setSearch] = useState("");
  const [birthDay, setBirthDay] = useState("");
  const { debounced: debouncedSearch, pending: searchPending } = useDebouncedSearch(search.trim());
  const { debounced: debouncedBirthDay, pending: birthDayPending } = useDebouncedSearch(birthDay);

  const [segment, setSegment] = useState("all");
  const [sort, setSort] = useState("lastVisit");
  const [dir, setDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState(null);

  const fetchList = useCallback(async () => {
    if (!token) return;
    try {
      const payload = {
        token,
        page: page + 1,
        limit: rowsPerPage,
        segment,
        sort,
        dir,
      };
      if (debouncedSearch) {
        if (/^\d+$/.test(debouncedSearch)) payload.tcNo = debouncedSearch;
        else payload.query = debouncedSearch;
      }
      if (debouncedBirthDay) payload.birthDay = debouncedBirthDay;
      const res = await listPatients(payload).unwrap();
      setItems(res?.data?.patients || []);
      setTotal(res?.data?.pagination?.total || 0);
      setCounts(res?.data?.segments || null);
    } catch (e) {
      showError(e?.data?.message || `${terms.patient} listesi alınamadı.`);
    }
  }, [
    token,
    page,
    rowsPerPage,
    segment,
    sort,
    dir,
    debouncedSearch,
    debouncedBirthDay,
    listPatients,
    showError,
    terms,
  ]);

  useEffect(() => {
    fetchList();
    // refreshKey: hasta eklendikten sonra yeniden çek
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchList, refreshKey]);

  const handleSort = (field) => {
    if (field === sort) setDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSort(field);
      setDir(DEFAULT_DIR[field] || "asc");
    }
    setPage(0);
  };

  const handleConfirmCancel = async () => {
    if (!cancelLinkId) return;
    try {
      await cancelLink({ token, linkId: cancelLinkId }).unwrap();
      showSuccess("Talep iptal edildi.");
      refetchPending();
    } catch (e) {
      showError(e?.data?.message || "İptal başarısız.");
    } finally {
      setCancelLinkId(null);
    }
  };

  const hasFilters = Boolean(
    debouncedSearch || debouncedBirthDay || segment !== "all",
  );
  const pendingCount = pendingLinks.length;
  const segmentOptions = SEGMENTS.filter((s) => treatmentModule || s.value !== "activeTreatment").map((s) => ({
    value: s.value,
    label: counts ? `${s.label} · ${counts[s.value] ?? 0}` : s.label,
  }));

  const canAdd = isSecretary && canCreatePatient;
  const addLink = (label) =>
    canAdd ? (
      <ButtonBase
        onClick={() => setWizardOpen(true)}
        sx={{
          font: "inherit",
          color: palette.primary,
          fontWeight: 600,
          textDecoration: "underline",
          textUnderlineOffset: 3,
          verticalAlign: "baseline",
        }}
      >
        {label}
      </ButtonBase>
    ) : null;

  const empty = (() => {
    if (debouncedSearch) {
      return {
        title: `${terms.patient} bulunamadı`,
        message: (
          <>
            “{debouncedSearch}” aramanızla eşleşen {terms.patientLower} yok. Aramayı değiştirin
            {canAdd ? <> veya {addLink(`yeni ${terms.patientLower} ekleyin`)}</> : null}.
          </>
        ),
      };
    }
    if (debouncedBirthDay)
      return {
        title: `${terms.patient} bulunamadı`,
        message: `Bu doğum tarihine sahip ${terms.patientLower} yok.`,
      };
    if (segment !== "all")
      return {
        title: `Bu filtrede ${terms.patientLower} yok`,
        message: segmentEmpty(terms)[segment],
      };
    return {
      title: `Henüz ${terms.patientLower} yok`,
      message: canAdd ? (
        <>İlk {terms.patientYourAccLower} {addLink("ekleyerek")} başlayın.</>
      ) : (
        `Klinikte kayıtlı ${terms.patientLower} bulunmuyor.`
      ),
    };
  })();

  const listTab = (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        bgcolor: COLORS.white,
      }}
    >
      {/* Araç satırı */}
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          pt: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          flexShrink: 0,
        }}
      >
        <SearchField
          placeholder="Ad, soyad veya TC no ile ara"
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(0);
          }}
          autoComplete="off"
          sx={{ width: { xs: "100%", sm: 340 } }}
        />
        <FormDatePicker
          placeholder="Doğum tarihi"
          value={birthDay}
          onChange={(v) => {
            setBirthDay(v);
            setPage(0);
          }}
          maxDate={new Date().toISOString().slice(0, 10)}
          sx={{ width: 180 }}
        />
        <Box sx={{ flex: 1 }} />
        <ButtonBase
          onClick={() => setPendingOpen(true)}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            px: 1.5,
            height: 36,
            borderRadius: 2,
            fontSize: TYPO.body,
            fontWeight: 600,
            border: `1px solid ${pendingCount ? alpha(COLORS.warning, 0.4) : COLORS.slate200}`,
            bgcolor: pendingCount ? COLORS.warningLightest : COLORS.white,
            color: pendingCount ? COLORS.warningDarker : COLORS.slate600,
            "&:hover": {
              bgcolor: pendingCount ? COLORS.warningLight : COLORS.slate50,
            },
          }}
        >
          <HourglassIcon sx={{ fontSize: 16 }} />
          Bekleyen talepler
          <Box
            component="span"
            sx={{
              minWidth: 20,
              height: 20,
              px: 0.5,
              borderRadius: 10,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: TYPO.caption,
              bgcolor: pendingCount ? COLORS.warning : COLORS.slate100,
              color: pendingCount ? COLORS.white : COLORS.slate500,
            }}
          >
            {pendingCount}
          </Box>
        </ButtonBase>
      </Box>

      {/* Hazır filtreler */}
      <Box sx={{ px: { xs: 2, md: 4 }, pt: 2, pb: 2, flexShrink: 0 }}>
        <ChoiceChipGroup
          ariaLabel={`${terms.patient} filtresi`}
          options={segmentOptions}
          value={segment}
          onChange={(v) => {
            setSegment(v);
            setPage(0);
          }}
        />
      </Box>

      {/* Bekleyen talep şeridi — yalnızca talep varken */}
      {pendingCount > 0 && (
        <ButtonBase
          onClick={() => setPendingOpen(true)}
          sx={{
            mx: { xs: 2, md: 4 },
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 2,
            justifyContent: "flex-start",
            gap: 1,
            bgcolor: COLORS.warningLightest,
            border: `1px solid ${alpha(COLORS.warning, 0.3)}`,
            flexShrink: 0,
          }}
        >
          <HourglassIcon sx={{ fontSize: 16, color: COLORS.warningDark }} />
          <Typography
            sx={{
              fontSize: TYPO.body,
              color: COLORS.warningDarker,
              flex: 1,
              textAlign: "left",
            }}
          >
            <strong>{pendingCount} {terms.patientLower}</strong> bağlantı talebinizi onaylamayı
            bekliyor. Onaylanınca listeye eklenir.
          </Typography>
          <Typography
            sx={{
              fontSize: TYPO.body,
              fontWeight: 600,
              color: COLORS.warningDarker,
            }}
          >
            Görüntüle
          </Typography>
          <ChevronRightIcon
            sx={{ fontSize: 18, color: COLORS.warningDarker }}
          />
        </ButtonBase>
      )}

      {/* Tablo — kalan alanı doldurur, kendi içinde kayar */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
        <PatientsTable
          items={items}
          totalCount={total}
          // Yazarken (debounce süresi) de iskelet: eski sonuç bir an bile görünmesin.
          isLoading={isLoading || searchPending || birthDayPending}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          onOpenDetail={(p) => navigate(`/Hastalar/${p._id}`)}
          hasFilters={hasFilters}
          empty={empty}
          sort={sort}
          dir={dir}
          onSort={handleSort}
        />
      </Box>
    </Box>
  );

  return (
    <>
      <TabbedPageShell
        icon={<FaUserInjured size={22} />}
        title={terms.patients}
        subtitle={`Klinik ${terms.patientsLower}ı, takip durumu ve dağılımı`}
        tabs={TABS.map((t) => ({ ...t, label: t.label.replace("Hasta", terms.patient) }))}
        action={
          canAdd ? (
            <ActionButton
              variant="add"
              palette={palette}
              size="medium"
              label={`${terms.patient} Ekle`}
              onClick={() => setWizardOpen(true)}
              disabled={!token}
            />
          ) : null
        }
      >
        {(tab) => (tab === "dagilim" ? <PatientDistributionTab /> : listTab)}
      </TabbedPageShell>

      <PendingRequestsDrawer
        open={pendingOpen}
        onClose={() => setPendingOpen(false)}
        links={pendingLinks}
        isLoading={pendingLoading}
        canCancel={canCreatePatient}
        onCancel={(id) => setCancelLinkId(id)}
        cancelling={cancelling}
      />

      <ConfirmDialog
        open={Boolean(cancelLinkId)}
        onClose={() => setCancelLinkId(null)}
        onConfirm={handleConfirmCancel}
        title="Talebi İptal Et"
        message="Talebi iptal etmek istediğinize emin misiniz?"
        confirmText="Talebi İptal Et"
        cancelText="Vazgeç"
        confirmVariant="error"
        loading={cancelling}
      />

      <AddPatientWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSuccess={() => {
          setRefreshKey((k) => k + 1);
          refetchPending();
        }}
      />
    </>
  );
}
